import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactElement, RefObject, useState} from 'react';

import {ContextMenuOption, view as ContextMenuView} from 'components/ContextMenu';
import {Strand} from 'constants/MolbioConstants';
import VisibilityToggle from 'molbio/sequence-features/VisibilityToggle';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHTER, BLUE_POWDER_LIGHT, GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {ColorSwatch} from 'ui-library/ColorSwatch';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

export interface FeatureDisplayData {
  cid: string;
  strand: Strand;
  name: string;
  color: string;
  visible?: boolean;
  hover?: boolean;
  editable: boolean;
  selected: boolean;
}

export interface FeatureRowItemProps<T extends FeatureDisplayData> {
  feature: T;
  editForm: ReactElement;
  contextMenuOptions: Array<ContextMenuOption>;
  getFeatureName?: (feature: T) => string;
  getLocationString: (feature: T) => string;
  onToggleVisibility?: (ids: Array<string>, visible: boolean) => void;
  onHover?: (id: string, hover: boolean) => void;
  onSelect: (id: string, selected: boolean) => void;
  rowRef?: RefObject<HTMLDivElement>;
}

export const BASE_FEATURE_ROW_HEIGHT = 43;

/**
 * A row of information corresponding to an annotation or translation in the features panel of the toolbar.
 * Handles editing, selecting, hovering and toggling visibility for the feature.
 */
function FeatureRowItem<T extends FeatureDisplayData>({
  feature,
  editForm,
  contextMenuOptions,
  getFeatureName,
  getLocationString,
  onToggleVisibility,
  onHover,
  onSelect,
  rowRef,
}: FeatureRowItemProps<T>): JSX.Element {
  const [useContextMenuStyle, setUseContextMenuStyle] = useState(false);

  function onShowContextMenu(e: ElementMouseEvent): void {
    setUseContextMenuStyle(true);
    ContextMenuView.open(e.nativeEvent, contextMenuOptions, true, () => {
      setUseContextMenuStyle(false);
    });
  }

  return (
    <div
      className={classNames({[css(styles.selectedSection)]: feature.selected})}
      {...getTestProps({
        component: 'SequenceFeatureRowItem',
        element: 'root',
      })}
      ref={rowRef}
    >
      <div
        className={classNames('feature-panel-row-header', css(styles.rowHeaderContainer), {
          [css(styles.hoveredRow)]: feature.hover || useContextMenuStyle,
          [css(styles.activeRow)]: feature.selected,
        })}
        onClick={() => {
          onSelect(feature.cid, !feature.selected);
        }}
        onMouseEnter={() => {
          onHover?.(feature.cid, true);
        }}
        onMouseLeave={() => {
          onHover?.(feature.cid, false);
        }}
        onContextMenu={onShowContextMenu}
      >
        <div className={css(styles.rowHeaderContent)}>
          <BenchlingIcon
            className={css(styles.rowCaret)}
            icon={feature.selected ? 'chevron-down' : 'chevron-right'}
          />
          <div className={css(styles.rowColorSwatch)}>
            <ColorSwatch fillColor={feature.color} small={true} />
          </div>
          <span className={css(styles.featureName)}>
            {getFeatureName ? getFeatureName(feature) : feature.name}
          </span>
          <span
            className={css(styles.featureRange)}
            {...getTestProps({
              component: 'SequenceFeatureRowItem',
              element: 'locationString',
            })}
          >
            {getLocationString(feature)}
          </span>
          {feature.visible != null && (
            <div className={css(styles.visibilityToggle)}>
              <VisibilityToggle
                isVisible={feature.visible}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleVisibility?.([feature.cid], !feature.visible);
                }}
              />
            </div>
          )}
        </div>
      </div>
      {feature.selected && <div className={css(styles.editForm)}>{editForm}</div>}
    </div>
  );
}

const styles = StyleSheet.create({
  rowHeaderContainer: {
    paddingLeft: '12px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  hoveredRow: {
    backgroundColor: GRAY_LIGHTER,
  },
  activeRow: {
    backgroundColor: BLUE_POWDER_LIGHT,
  },
  rowHeaderContent: {
    display: 'flex',
    padding: '12px 4px 12px 0',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  featureName: {
    marginRight: '8px',
    maxWidth: '40%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  featureRange: {
    color: GRAY_DARKER,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '50%',
  },
  visibilityToggle: {
    margin: 'auto 4px auto auto',
  },
  rowCaret: {
    margin: 'auto 0',
    color: GRAY_DARKER,
    marginRight: '12px',
  },
  rowColorSwatch: {
    margin: 'auto 8px auto 0',
  },
  selectedSection: {
    borderLeft: `2px solid ${BLUE_LIGHTER}`,
  },
  editForm: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
});

export default FeatureRowItem;
